import React, { useEffect, useState } from 'react';
import { Unstable_Grid2 as Grid, Card, CardContent, Typography, Box, useMediaQuery } from '@mui/material';
import { colors } from '../styles/fontStyles';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { DisplayDetailsForValue, ReportDetails } from '../types/reports';
import { DETECTED, NOT_DETECTED, ABOVE_RANGE, BELOW_RANGE, INCONCLUSIVE } from '../constants/report';
import { CompoundCardProps, CompoundCardsListProps } from '../types/reports';
import { isNotesOrDisclaimer, getTestName, getDisplayLabel } from '../utils/reports';
import useBackendApi from '../hooks/useBackendApi';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import CompoundDetailsResult from './CompoundDetailsResult';
import CompoundLabelChip from './details/CompoundLabelChip';

const backendURL = process.env.REACT_APP_BACKEND_URL;

interface CircleProps {
  radius: number;
  color: string;
}

const Circle: React.FC<CircleProps> = ({ radius, color }) => {
  const circleStyle = {
    width: `${radius * 2}px`,
    height: `${radius * 2}px`,
    backgroundColor: color,
    borderRadius: '50%',
    display: 'inline-block',
    align: 'center',
  };

  return <div style={circleStyle}></div>;
};

type RenderIndicatorProps = {
  ddfvLabel: string;
  radius: number;
};

const RenderIndicator = ({ ddfvLabel, radius }: RenderIndicatorProps) => {
  const { theme } = useThemeContext();
  switch (ddfvLabel) {
    case NOT_DETECTED:
      return (
        <Box>
          <img
            src={theme.custom.report.details.compoundCardsList.indicator.not_detected.value}
            alt="not detected"
            style={{ margin: '10px' }}
          />
        </Box>
      );
    case BELOW_RANGE:
      return (
        <Box>
          <img
            src={theme.custom.report.details.compoundCardsList.indicator.below_range.value}
            alt="below range"
            style={{ margin: '10px' }}
          />
        </Box>
      );
    case ABOVE_RANGE:
      return (
        <Box>
          <img
            src={theme.custom.report.details.compoundCardsList.indicator.above_range.value}
            alt="above range"
            style={{ marginTop: '4px' }}
          />
        </Box>
      );
    case INCONCLUSIVE:
      return (
        <Box>
          <img src={theme.custom.report.details.compoundCardsList.indicator.inconclusive.value} alt="inconclusive" />
        </Box>
      );
    default:
      // For DETECTED or other labels, render a Circle
      return <Circle radius={radius} color={theme.custom.report.details.compoundCardsList.indicator.detected.value} />;
  }
};

const renderUnitDetails = (ddfv: DisplayDetailsForValue, theme: any) => {
  if (
    ddfv.label === NOT_DETECTED ||
    ddfv.label === ABOVE_RANGE ||
    ddfv.label === BELOW_RANGE ||
    ddfv.label === INCONCLUSIVE
  ) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            color: theme.custom.report.details.compoundCardsList.cardContainer.content.unitDetails.noValue.color,
            fontSize: '28px',
            fontWeight: '400',
            lineHeight: '36px',
          }}
        >
          {'-'}
        </Typography>
        <Typography
          sx={{ fontSize: '12px', fontWeight: '400', lineHeight: '16px' }}
          color={theme.custom.report.details.compoundCardsList.cardContainer.content.unitDetails.name.color}
        >
          {ddfv.testUnit}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: '400',
          lineHeight: '36px',
        }}
        color={theme.custom.report.details.compoundCardsList.cardContainer.content.unitDetails.value.color}
      >
        {` ${ddfv.value} `}
      </Typography>
      <Typography
        sx={{ fontSize: '12px', fontWeight: '400', lineHeight: '16px' }}
        color={theme.custom.report.details.compoundCardsList.cardContainer.content.unitDetails.name.color}
      >
        {ddfv.testUnit}
      </Typography>
    </Box>
  );
};

const CompoundCardResult = ({
  isMobile,
  radius,
  ...ddfv
}: DisplayDetailsForValue & { isMobile: boolean; radius: number }) => {
  const { theme, isDarkMode } = useThemeContext();
  const label = getDisplayLabel(ddfv.label)
  let lightThemeStyles = {};
  if (!isDarkMode) {
    lightThemeStyles = {
      boxShadow: 'none',
      // this color is only needed for the light theme
      border: 'solid 1px ' + colors.gray550,
    };
  }

  return (
    <Card
      sx={{
        background: theme.custom.report.details.compoundCardsList.cardContainer.background,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '8px',
        padding: '8px 12px',
        ...lightThemeStyles,
      }}
    >
      <CardContent sx={{ padding: '0 !important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{
                color: theme.custom.report.details.compoundCardsList.cardContainer.content.compoundName.color,
                fontSize: '14px',
                fontWeight: '500',
                lineHeight: '20px',
                letterSpacing: '0.1px',
                marginRight: '1px',
              }}
              gutterBottom
            >
              {`${ddfv?.testNameAbbr} `}
            </Typography>
            {renderUnitDetails(ddfv, theme)}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <CompoundLabelChip
              label={label}
              backgroundColor={theme.custom.report.dataDecoration[ddfv.detectionStatus].backgroundColor}
              fontColor={theme.custom.report.dataDecoration[ddfv.detectionStatus].fillFontColor}
            />
            <RenderIndicator ddfvLabel={ddfv.label} radius={radius} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CompoundCardsList = ({ compoundDataList }: CompoundCardsListProps) => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isCompoundDetailsLoading, setIsCompoundDetailsLoading] = useState<boolean>(false);
  const helpId = 'pfas_table_components_info';
  const [isDescComponentOpen, setIsDescComponentOpen] = useState(false);
  const [selectedCompoundIndex, setSelectedCompoundIndex] = useState<number>(0);
  const [reportCompoundDetails, setReportCompoundDetails] = useState<DisplayDetailsForValue | null>(null);
  const [reportDetails, setReportDetails] = useState<Record<string, ReportDetails>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { apiFetchRequest } = useBackendApi();
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  const MIN_CIRCLE_RADIUS = 4;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const compoundResponse = await apiFetchRequest({ endpoint: `${backendURL}/help/${helpId}` });
        const jsonString = compoundResponse.body.h.replace(/'/g, '"');
        const compoundData = JSON.parse(jsonString.replace(/False/g, 'false').replace(/True/g, 'true'));
        setReportDetails(compoundData);
      } catch (error) {
        console.error('Error fetching details:', error);
        addAlertMessages({ message: 'Error fetching details', severityType: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, []);

  const handleOpen = (ddfv: DisplayDetailsForValue, compoundElementIndex: number) => {
    setReportCompoundDetails(ddfv);
    setIsDescComponentOpen(true);
    setIsCompoundDetailsLoading(true);
    setSelectedCompoundIndex(compoundElementIndex);
  };

  const parseValue = (value: string | null): number => {
    if (value === null) return -Infinity;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? -Infinity : parsed;
  };

  const sortedCompoundDataList: CompoundCardProps[] = compoundDataList.sort((a, b) => {
    const valueA = parseValue(a.value);
    const valueB = parseValue(b.value);
    return valueB - valueA;
  });

  const showLoadingSpinner = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          margin: '10px 0',
        }}
      >
        <GradientCircularProgress />
      </Box>
    );
  };

  if (isLoading) {
    return showLoadingSpinner();
  }

  /**
    Calculates the radius of a circle based on the detected value's relative position
    between a minimum and maximum range. The radius scales linearly between 10 and 20.
    If detected value is min, the radius is 10.
    If detected value is max, the radius is 20.
    For the rest of the values the radius is midway between 10 and 20.
  */
  const calculateRadius = (value: number | null, min: number, max: number): number => {
    if (value === null || isNaN(value)) {
      return MIN_CIRCLE_RADIUS; // Default radius for invalid or null values
    }

    if (min === max) {
      return 10;
    }

    return ((value - min) / (max - min)) * 10 + 10; // Scale between 10 and 20
  };

  const detectedValues = sortedCompoundDataList
    .filter((item) => item.ddfv.label === DETECTED && item.ddfv.value !== null)
    .map((item) => parseFloat(item.ddfv.value) || 0);

  const minDetectedValue = Math.min(...detectedValues);
  const maxDetectedValue = Math.max(...detectedValues);

  const renderCompoundDetailsLoading = () => {
    if (!isCompoundDetailsLoading) return <></>;

    return <GradientCircularProgressContainer loadingMessage={'Loading compound details result'} />;
  };

  const getDetectionFreqFromReportCompoundDetails = () => {
    const currentCompoundDetails = sortedCompoundDataList[selectedCompoundIndex];
    const helpId = getTestName(currentCompoundDetails.reportFormat.testName);
    return reportDetails[helpId]?.detection_freq;
  };

  const renderMobileCompoundDetailsWithPagination = () => {
    if (!isDescComponentOpen) return <></>;

    const sortedCompoundDataFiltered = sortedCompoundDataList.filter((item) => {
      const testNameAbbr = getTestName(item.reportFormat.testName);
      const testUnit = item.reportFormat.testUnit;
      return !isNotesOrDisclaimer(testNameAbbr, testUnit);
    });

    return (
      <CompoundDetailsResult
        setIsDescComponentOpen={setIsDescComponentOpen}
        detectionFreq={getDetectionFreqFromReportCompoundDetails()}
        setSelectedCompoundIndex={setSelectedCompoundIndex}
        selectedCompoundIndex={selectedCompoundIndex}
        sortedCompoundDataList={sortedCompoundDataFiltered}
        setIsCompoundDetailsLoading={setIsCompoundDetailsLoading}
      />
    );
  };

  const renderCompoundCardsList = () => {
    // We don't render the list if the compounds details are opened
    if (isDescComponentOpen) return <></>;

    return (
      <Grid container spacing={2}>
        {sortedCompoundDataList.map((item, index) => {
          const helpId = getTestName(item.reportFormat.testName);
          const testUnit = item.reportFormat.testUnit;
          if (isNotesOrDisclaimer(helpId, testUnit)) {
            return <></>;
          }

          const radius =
            item.ddfv.label === DETECTED
              ? calculateRadius(parseFloat(item.ddfv.value), minDetectedValue, maxDetectedValue)
              : MIN_CIRCLE_RADIUS;

          return (
            <Grid
              sx={{ cursor: 'pointer' }}
              onClick={() => handleOpen(item.ddfv, index)}
              xs={6}
              sm={4}
              md={3}
              key={`${reportCompoundDetails?.testName}-${index}`}
            >
              <CompoundCardResult {...item.ddfv} isMobile={isMobile} radius={radius} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box>
      {renderSnackBarAlertComponent()}
      {renderCompoundDetailsLoading()}
      {renderCompoundCardsList()}
      {renderMobileCompoundDetailsWithPagination()}
    </Box>
  );
};

export default CompoundCardsList;
